import React, { useEffect, useState } from "react";
import "~/css/Modal.css";
import "~/css/Button.css";
import "~/css/QRSetting.css";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { PlaySound } from "~/SoundUtils";
import { volumeAtom, isSettingOpenAtom } from "~/Atom";
import { qrCodeTitleAtom, qrCodeUrlAtom } from "~/AtomWithDynamoDB";
import { QRCodeSVG } from "qrcode.react";
import { showNotificationDialog } from "~/utils/notificationDialogUtils";

const InputTextForm: React.FC<{
  inputText: string;
  setInputText: (value: string) => void;
  label: string;
}> = ({ inputText, setInputText, label }) => {
  return (
    <>
      <div className="font-default-normal-20">{label}</div>
      <input
        type="text"
        value={inputText}
        onChange={(e) => setInputText(e.target.value)}
        placeholder={label}
        className="form-control"
      />
    </>
  );
};

/**
 * 二次元コードモーダルコンポーネント
 */
const QRCode: React.FC = () => {
  const { t } = useTranslation();
  const [isSettingOpen] = useAtom(isSettingOpenAtom);
  const [volume] = useAtom(volumeAtom);
  const [qrCodeURL, setQrCodeURL] = useAtom(qrCodeUrlAtom); // 二次元コードURLをAtomで管理
  const [qrCodeTitle, setQrCodeTitle] = useAtom(qrCodeTitleAtom); // 二次元コードタイトルをAtomで管理
  const [inputURL, setInputURL] = useState<string>(""); // 入力用の二次元コードURL
  const [inputTitle, setInputTitle] = useState<string>(""); // 入力用のタイトル
  const [qrSize, setQrSize] = useState<number>(200); // 二次元コードのサイズ

  // デバイスの向きを検知
  useEffect(() => {
    const checkOrientation = () => {
      const containerWidth = window.innerWidth;
      const containerHeight = window.innerHeight;

      if (window.innerHeight > window.innerWidth) {
        // 縦向き
        setQrSize(containerWidth * 0.3);
      } else {
        // 横向き
        setQrSize(containerHeight * 0.3);
      }
    };
    checkOrientation();

    // リサイズイベントリスナー
    window.addEventListener("resize", checkOrientation);
    window.addEventListener("orientationchange", checkOrientation);

    // クリーンアップ
    return () => {
      window.removeEventListener("resize", checkOrientation);
      window.removeEventListener("orientationchange", checkOrientation);
    };
  }, []);

  // Atomから二次元コードURL、タイトルを取得して入力フォームに反映
  useEffect(() => {
    if (qrCodeURL && qrCodeTitle) {
      setInputURL(qrCodeURL);
      setInputTitle(qrCodeTitle);
    }
  }, [qrCodeURL, qrCodeTitle]);

  // 登録ボタンがクリックされた時の処理
  const handleRegisterClick = () => {
    // Atomを更新すると、atomWithDynamoDBが自動的にlocalStorageとDynamoDBを管理
    setQrCodeURL(inputURL);
    setQrCodeTitle(inputTitle);
    showNotificationDialog(t("saved"), "success");
  };

  // 二次元コードが登録されているかどうかを確認
  const isQrCodeRegistered = () => {
    return Boolean(qrCodeURL && qrCodeTitle);
  };

  return (
    <>
      {isSettingOpen ? (
        <>
          <div className="qr-container">
            {isQrCodeRegistered() ? (
              <QRCodeSVG value={qrCodeURL} size={qrSize} />
            ) : (
              <h4>{t("qrCodeNotRegister")}</h4>
            )}
            <div className="qr-input-grid">
              <InputTextForm
                inputText={inputTitle}
                setInputText={setInputTitle}
                label={t("title")}
              />
              <InputTextForm inputText={inputURL} setInputText={setInputURL} label={t("URL")} />
            </div>
          </div>
          <div className="qr-button-container">
            <button
              onClick={handleRegisterClick}
              onMouseUp={() => PlaySound("push3", volume)}
              className="button-1 flexible"
              disabled={!inputURL || !inputTitle}
            >
              {t("register")}
            </button>
          </div>
        </>
      ) : isQrCodeRegistered() ? (
        <>
          <div className="qr-container">
            <div className="font-default-normal-20 qr-title">{qrCodeTitle}</div>
            <QRCodeSVG value={qrCodeURL} size={qrSize} />
          </div>
        </>
      ) : (
        <h2>{t("qrCodeNotRegister")}</h2>
      )}
    </>
  );
};

export default QRCode;
